import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccounts, getRtiSettings } from 'redux/slicers/accounts';
import { useAppSelector } from 'redux/store';
import { AccountFormData, SelectOptions, ProductSelectOption } from '../types';
import { DEFAULT_FORM_DATA, FORM_STATE_OPTIONS, INITIAL_SELECT_OPTION, DATATYPE } from '../constant';
import { getInitialAccountType, mapToSelectOptions } from '../utils';
import { IFormattedAccount } from 'models/Account';
import { Products, REBRANDED_PRODUCT_NAMES } from 'services/api/consts';
import { useSplit } from 'hooks';
import { SPLIT_CONSTANTS } from 'services/split.io';

const ERROR_MESSAGES = {
    FORM_UPDATE_FAILED: 'An error occurred while updating form data',
    FORM_INIT_FAILED: 'Failed to initialize form data',
    RTI_UPDATE_FAILED: 'Failed to update defend data type',
} as const;

/**
 * Helper function to handle errors consistently
 */
const handleError = (err: unknown, fallbackMessage: string): string => {
    return err instanceof Error ? err.message : fallbackMessage;
};

interface UseAccountFormProps {
    currentAccount: IFormattedAccount | null;
    action: string;
}

interface UseAccountFormReturn {
    formData: AccountFormData;
    selectOptions: SelectOptions;
    handleFormChange: (changes: Partial<AccountFormData>) => void;
    createNewAccount: boolean;
    isLoading: boolean;
    error: string | null;
}

/**
 * Custom hook for managing account form state and operations
 *
 * Features:
 * - Handles form data initialization and updates
 * - Manages select options for dropdowns
 * - Automatically syncs Salesforce account data
 * - Manages RTI settings and defend data types
 * - Provides loading states and error handling
 *
 * @param currentAccount - The account being edited (null for new accounts)
 * @param action - Form action type ('create' or 'edit')
 * @returns Form state, handlers, and status information
 */
export const useAccountForm = ({ currentAccount, action }: UseAccountFormProps): UseAccountFormReturn => {
    const salesforceAccounts = useAppSelector((state) => state.accounts.salesforceAccounts);
    const { products = [], regions = [], dataLocations = [] } = useSelector(selectAccounts);
    const rtiSettings = useAppSelector((state) => state.accounts.rtiSettings);
    const dispatch = useDispatch();
    const { isOn: isAgentIntentRebrand } = useSplit(SPLIT_CONSTANTS.PD_AGENT_INTENT_REBRAND);

    // State
    const [formData, setFormData] = useState<AccountFormData>(DEFAULT_FORM_DATA);
    const [selectOptions, setSelectOptions] = useState<SelectOptions>(INITIAL_SELECT_OPTION);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Memoized values
    const createNewAccount = useMemo(() => action === FORM_STATE_OPTIONS.create, [action]);

    const initialSalesforceAccount = useMemo(() => currentAccount?.salesforceId || '', [currentAccount?.salesforceId]);

    const initialSalesforceObj = useMemo(() => salesforceAccounts.find((account) => account.ID === initialSalesforceAccount), [salesforceAccounts, initialSalesforceAccount]);

    const accountData = useMemo(() => (currentAccount || {}) as IFormattedAccount, [currentAccount]);

    const networkId = useMemo(() => currentAccount?.networkId || currentAccount?.networks?.[0]?.id, [currentAccount?.networkId, currentAccount?.networks]);

    const showDataType = useMemo(() => formData.activeLicenses.includes(Products.OSS), [formData.activeLicenses]);

    const memoizedSelectOptions = useMemo(() => {
        const productOptions: ProductSelectOption[] = products.map((p) => ({
            value: p.id,
            label: isAgentIntentRebrand ? (REBRANDED_PRODUCT_NAMES[p.id as Products] || p.name) : p.name,
        }));

        const salesforceAccountOptions = salesforceAccounts.map(({ ID, Name }) => ({
            value: ID,
            label: Name,
        }));

        return {
            region: mapToSelectOptions(regions),
            activeLicenses: productOptions,
            dataLocation: mapToSelectOptions(dataLocations),
            salesforceAccounts: salesforceAccountOptions,
            accountType: INITIAL_SELECT_OPTION.accountType,
            defendDataType: INITIAL_SELECT_OPTION.defendDataType,
        };
    }, [products, regions, dataLocations, salesforceAccounts, isAgentIntentRebrand]);

    const handleFormChange = useCallback(
        (changes: Partial<AccountFormData>) => {
            try {
                setError(null);

                if (changes.salesforceAccount) {
                    const salesforceAccount = salesforceAccounts.find((account) => account.ID === changes.salesforceAccount);

                    setFormData((prev) => ({
                        ...prev,
                        ...changes,
                        accountStatus: salesforceAccount?.AccountStatus || '',
                        accountDomain: salesforceAccount?.Domain || '',
                        accountSE: salesforceAccount?.SalesEngineer || '',
                        accountCSM: salesforceAccount?.CSM || '',
                        accountAE: salesforceAccount?.AE || '',
                    }));
                } else {
                    setFormData((prev) => ({ ...prev, ...changes }));
                }
            } catch (err) {
                setError(handleError(err, ERROR_MESSAGES.FORM_UPDATE_FAILED));
            }
        },
        [salesforceAccounts]
    );

    // Initialize form data when current account changes
    useEffect(() => {
        if (!currentAccount) return;

        try {
            setIsLoading(true);
            setError(null);

            const { regionId, products, name, dataLocationId, accountType, networks, email, active } = currentAccount;
            const whiteLabelDomain = networks?.[0]?.whiteLabelDomain?.domain || '';
            const servingDomain = createNewAccount ? '' : whiteLabelDomain;
            const deduceConfiguration = networks?.[0]?.deduceConfiguration || null;

            setFormData((prev) => ({
                ...prev,
                ...DEFAULT_FORM_DATA,
                ...accountData,
                servingDomain,
                region: String(regionId || ''),
                accountName: name || '',
                accountType: getInitialAccountType(accountType),
                salesforceAccount: initialSalesforceAccount,
                accountStatus: initialSalesforceObj?.AccountStatus || '',
                accountDomain: initialSalesforceObj?.Domain || '',
                accountSE: initialSalesforceObj?.SalesEngineer || '',
                accountCSM: initialSalesforceObj?.CSM || '',
                accountAE: initialSalesforceObj?.AE || '',
                activeLicenses: Array.isArray(products) ? products.map(({ value }) => value as Products) : [],
                defendDataType: (accountData as { defendDataType?: string[] }).defendDataType || [],
                dataLocation: String(dataLocationId || ''),
                accountEmail: email || '',
                cheqAccountStatus: Number(active),
                deduceEnabled: deduceConfiguration ? 1 : 0,
                deduceSite: deduceConfiguration?.site || '',
                deduceApiKey: deduceConfiguration?.apiKey || '',
            }));
        } catch (err) {
            setError(handleError(err, ERROR_MESSAGES.FORM_INIT_FAILED));
        } finally {
            setIsLoading(false);
        }
    }, [currentAccount, initialSalesforceObj, accountData, createNewAccount, initialSalesforceAccount]);

    useEffect(() => {
        setSelectOptions(memoizedSelectOptions);
    }, [memoizedSelectOptions]);

    // Populate deduce fields when networks are loaded from getAssociatedNetworks
    useEffect(() => {
        const deduceConfiguration = currentAccount?.networks?.[0]?.deduceConfiguration;
        if (!deduceConfiguration) return;

        setFormData((prev) => ({
            ...prev,
            deduceEnabled: 1,
            deduceSite: deduceConfiguration.site || '',
            deduceApiKey: deduceConfiguration.apiKey || '',
        }));
    }, [currentAccount?.networks]);

    // Fetch RTI settings when networkId and showDataType are available
    useEffect(() => {
        if (networkId && showDataType && !createNewAccount) {
            setIsLoading(true);
            dispatch(getRtiSettings(networkId));
        }
    }, [dispatch, networkId, createNewAccount]);

    // Update defendDataType based on RTI settings
    useEffect(() => {
        if (rtiSettings && networkId === rtiSettings.networkId && showDataType) {
            try {
                const rtiBasedDefendDataTypes: string[] = [];

                if (rtiSettings.isApiIntegration) {
                    rtiBasedDefendDataTypes.push(DATATYPE.SERVER);
                }
                if (rtiSettings.isClientSideIntegration) {
                    rtiBasedDefendDataTypes.push(DATATYPE.CLIENT);
                }

                setFormData((prev) => ({
                    ...prev,
                    defendDataType: rtiBasedDefendDataTypes,
                }));
            } catch (err) {
                setError(handleError(err, ERROR_MESSAGES.RTI_UPDATE_FAILED));
            } finally {
                setIsLoading(false);
            }
        }
    }, [rtiSettings, networkId, showDataType, accountData]);

    return {
        formData,
        selectOptions,
        handleFormChange,
        createNewAccount,
        isLoading,
        error,
    };
};
